// FinancePage.jsx — Admin-only Personal Finance Dashboard
// Place in src/ folder. Data stored in Firestore "finance" collection.

import { useState, useEffect } from "react";
import { db } from "./firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";

const S = {
  page: { minHeight: "100vh", background: "#f0f4ff", padding: "24px 16px", fontFamily: "Arial, sans-serif" },
  topBar: { background: "linear-gradient(135deg,#1a237e,#1565c0)", borderRadius: 16, padding: "20px 28px", marginBottom: 24, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 },
  title: { fontSize: "1.4rem", fontWeight: 800, margin: 0 },
  sub: { fontSize: "0.85rem", opacity: 0.8, marginTop: 4 },
  tabs: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 },
  tab: (active, color) => ({ padding: "8px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 700, fontSize: "0.82rem", background: active ? color : "#e0e7ff", color: active ? "#fff" : "#555", transition: "all 0.2s" }),
  card: (border) => ({ background: "#fff", borderRadius: 14, padding: "20px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", borderTop: `4px solid ${border}`, marginBottom: 20 }),
  cardTitle: (color) => ({ fontSize: "1rem", fontWeight: 800, color, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }),
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12 },
  statBox: (bg) => ({ background: bg, borderRadius: 12, padding: "16px", textAlign: "center" }),
  statNum: (color) => ({ fontSize: "1.6rem", fontWeight: 800, color }),
  statLbl: { fontSize: "0.78rem", color: "#666", marginTop: 4 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f0f0f0" },
  lbl: { fontSize: "0.88rem", color: "#555", fontWeight: 600 },
  val: (color) => ({ fontSize: "0.92rem", fontWeight: 700, color }),
  input: { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #ddd", fontSize: "0.9rem", boxSizing: "border-box", marginBottom: 8 },
  addBtn: (color) => ({ background: color, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" }),
  deleteBtn: { background: "#ffebee", color: "#c62828", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: "0.78rem", fontWeight: 700 },
  editBtn: { background: "#e3f2fd", color: "#1565c0", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: "0.78rem", fontWeight: 700 },
  tag: (color) => ({ background: color, color: "#fff", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700 }),
};

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const CUR_MONTH = new Date().getMonth();
const CUR_YEAR  = new Date().getFullYear();

const DEFAULT_DATA = {
  income: [
    { id: 1, label: "Dr. Devaraju Salary", amount: 75000, status: "Active" },
    { id: 2, label: "Wife's Salary",        amount: 25000, status: "Active" },
    { id: 3, label: "House Rent Income",    amount: 8000,  status: "Active" },
    { id: 4, label: "Other Income",         amount: 0,     status: "Active" },
  ],
  expenses: [
    { id: 1,  cat: "Education",  label: "School Fee — Child 1 (11th)", amount: 3000 },
    { id: 2,  cat: "Education",  label: "School Fee — Child 2 (3rd)",  amount: 2000 },
    { id: 3,  cat: "Education",  label: "Hostel Fee",                  amount: 4000 },
    { id: 4,  cat: "Household",  label: "Groceries & Vegetables",      amount: 5000 },
    { id: 5,  cat: "Household",  label: "Household Items",             amount: 1000 },
    { id: 6,  cat: "Utilities",  label: "Electricity Bill",            amount: 1500 },
    { id: 7,  cat: "Utilities",  label: "Gas (LPG Cylinder)",          amount: 900  },
    { id: 8,  cat: "Utilities",  label: "Mobile Bill",                 amount: 699  },
    { id: 9,  cat: "Utilities",  label: "Internet / Broadband",        amount: 699  },
    { id: 10, cat: "Transport",  label: "Car Diesel",                  amount: 3000 },
    { id: 11, cat: "Transport",  label: "Bike Petrol",                 amount: 1000 },
    { id: 12, cat: "Transport",  label: "FASTag / Toll",               amount: 300  },
    { id: 13, cat: "Insurance",  label: "Life Insurance Premium",      amount: 2000 },
    { id: 14, cat: "Insurance",  label: "Health Insurance",            amount: 1500 },
    { id: 15, cat: "Savings",    label: "SIP / Mutual Funds",          amount: 3000 },
    { id: 16, cat: "Savings",    label: "RD / FD",                    amount: 2000 },
    { id: 17, cat: "Misc",       label: "Miscellaneous",              amount: 1000 },
  ],
  emi: [
    { id: 1, label: "Home Loan EMI", amount: 0, bank: "Add bank", dueDate: "Add date", status: "Active" },
    { id: 2, label: "Car Loan EMI",  amount: 0, bank: "Add bank", dueDate: "Add date", status: "None"   },
  ],
  insurance: [
    { id: 1, label: "Life Insurance",   company: "Add company", premium: 0, dueDate: "Add date", policyNo: "Add no." },
    { id: 2, label: "Health Insurance", company: "Add company", premium: 0, dueDate: "Add date", policyNo: "Add no." },
    { id: 3, label: "Car Insurance",    company: "Add company", premium: 0, dueDate: "Add date", policyNo: "Add no." },
    { id: 4, label: "Bike Insurance",   company: "Add company", premium: 0, dueDate: "Add date", policyNo: "Add no." },
  ],
  reminders: [
    { id: 1, label: "Car Insurance Renewal", dueDate: "Add date", amount: 0,   cat: "Vehicle"   },
    { id: 2, label: "Bike PUC Renewal",       dueDate: "Add date", amount: 300, cat: "Vehicle"   },
    { id: 3, label: "Income Tax Filing",       dueDate: "31 July",  amount: 0,   cat: "Tax"       },
    { id: 4, label: "School Fee — Child 1",   dueDate: "Add date", amount: 0,   cat: "Education" },
    { id: 5, label: "Property Tax",           dueDate: "Add date", amount: 0,   cat: "Property"  },
  ],
  celebrations: [
    { id: 1, label: "Dr. Devaraju Birthday", date: "01 Aug",    budget: 1000, cat: "Birthday"   },
    { id: 2, label: "Wedding Anniversary",   date: "Add date",  budget: 2000, cat: "Anniversary" },
    { id: 3, label: "Child 1 Birthday",      date: "Add date",  budget: 1500, cat: "Birthday"   },
    { id: 4, label: "Child 2 Birthday",      date: "Add date",  budget: 1500, cat: "Birthday"   },
    { id: 5, label: "Ugadi Festival",        date: "Mar/Apr",   budget: 2000, cat: "Festival"   },
    { id: 6, label: "Diwali",               date: "Oct/Nov",   budget: 3000, cat: "Festival"   },
    { id: 7, label: "Weekend Outings",       date: "Monthly",   budget: 1500, cat: "Leisure"    },
    { id: 8, label: "Visitors / Guests",     date: "As needed", budget: 500,  cat: "Social"     },
  ],
  travel: [
    { id: 1, label: "MRS-2011, San Francisco, USA", date: "Apr 2011", expense: 0, purpose: "Conference"   },
    { id: 2, label: "ICACS-24, Krakow, Poland",     date: "Jul 2010", expense: 0, purpose: "Conference"   },
    { id: 3, label: "WINS Workshop 2025",            date: "Feb 2025", expense: 0, purpose: "Invited Talk" },
  ],
  rent: [
    { id: 1, label: "Flat Rent Income", tenant: "Add tenant", amount: 8000, dueDate: "1st of month", status: "Active" },
  ],
  certificates: [
    { id: 1, label: "PhD Certificate",     person: "Dr. Devaraju", issuer: "Univ. of Hyderabad",     year: "2012", location: "Physical + Drive" },
    { id: 2, label: "MSc Certificate",     person: "Dr. Devaraju", issuer: "Univ. of Hyderabad",     year: "2003", location: "Physical + Drive" },
    { id: 3, label: "Aadhaar Card",        person: "Dr. Devaraju", issuer: "UIDAI",                  year: "",     location: "Physical + Drive" },
    { id: 4, label: "PAN Card",            person: "Dr. Devaraju", issuer: "Income Tax Dept.",       year: "",     location: "Physical + Drive" },
    { id: 5, label: "Aadhaar Card",        person: "Swapna",       issuer: "UIDAI",                  year: "",     location: "Physical + Drive" },
    { id: 6, label: "Postdoc Certificate", person: "Dr. Devaraju", issuer: "Univ. of Padova, Italy", year: "2016", location: "Physical + Drive" },
  ],
  customButtons: [
    { id: 1, label: "📞 Bank Helpline", value: "1800-XXX-XXXX",               type: "phone" },
    { id: 2, label: "🏦 Net Banking",   value: "https://onlinesbi.sbi.co.in", type: "link"  },
  ],
  monthlyLog: [],
  // format: [{ id, month(0-11), year, date, cat, label, amount, note }]
};

const EXP_CATS   = ["Education","Household","Utilities","Transport","Insurance","Savings","Misc"];
const CAT_COLORS = { Education:"#1565c0", Household:"#2e7d32", Utilities:"#00838f", Transport:"#e65100", Insurance:"#c62828", Savings:"#6a1b9a", Misc:"#757575" };

const TABS = [
  { key:"overview",     label:"📊 Overview",      color:"#1565c0" },
  { key:"monthly",      label:"📆 Monthly Log",    color:"#00897b" },
  { key:"income",       label:"💰 Income",         color:"#2e7d32" },
  { key:"expenses",     label:"📤 Budget",         color:"#e65100" },
  { key:"emi",          label:"🏦 EMI & Loans",    color:"#880e4f" },
  { key:"rent",         label:"🏠 Rent",           color:"#00838f" },
  { key:"insurance",    label:"🛡️ Insurance",      color:"#c62828" },
  { key:"reminders",    label:"📅 Reminders",      color:"#f57f17" },
  { key:"celebrations", label:"🎉 Celebrations",   color:"#ad1457" },
  { key:"travel",       label:"✈️ Travel",         color:"#1a237e" },
  { key:"certificates", label:"📁 Certificates",   color:"#4a148c" },
  { key:"quick",        label:"⚡ Quick Buttons",  color:"#37474f" },
];

export default function FinancePage({ setPage }) {
  const [tab,     setTab]     = useState("overview");
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "finance", "dashboard"));
        if (snap.exists()) {
          const d = snap.data();
          setData({ ...DEFAULT_DATA, ...d, monthlyLog: d.monthlyLog || [] });
        } else {
          setData(DEFAULT_DATA);
        }
      } catch (e) { setData(DEFAULT_DATA); }
      setLoading(false);
    };
    load();
  }, []);

  const save = async (newData) => {
    setSaving(true);
    try { await setDoc(doc(db, "finance", "dashboard"), newData); setData(newData); }
    catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const updateSection = (section, newArr) => save({ ...data, [section]: newArr });

  if (loading) return <div style={{ textAlign:"center", padding:60, fontSize:"1.2rem", color:"#1a237e" }}>Loading Finance Dashboard...</div>;

  const totalIncome    = data.income.filter(i => i.status === "Active").reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalBudget    = data.expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalEMI       = data.emi.filter(e => e.status === "Active").reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalInsurance = data.insurance.reduce((s, i) => s + Number(i.premium || 0), 0);
  const totalRent      = data.rent.filter(r => r.status === "Active").reduce((s, r) => s + Number(r.amount || 0), 0);
  const netSavings     = totalIncome - totalBudget - totalEMI;

  const curMonthLog    = (data.monthlyLog || []).filter(e => e.month === CUR_MONTH && e.year === CUR_YEAR);
  const curMonthActual = curMonthLog.reduce((s, e) => s + Number(e.amount || 0), 0);
  const curMonthDiff   = totalBudget - curMonthActual;

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <div>
          <div style={S.title}>💼 Personal Finance Dashboard</div>
          <div style={S.sub}>Dr. Devaraju Gurram — Admin Only — FY 2025-26</div>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {saving && <span style={{ color:"#ffd600", fontSize:"0.85rem" }}>Saving...</span>}
          <button style={S.addBtn("#ffd600")} onClick={() => setPage("home")}>← Home</button>
        </div>
      </div>

      <div style={S.tabs}>
        {TABS.map(t => <button key={t.key} style={S.tab(tab === t.key, t.color)} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>

      {/* ══ OVERVIEW ══ */}
      {tab === "overview" && (
        <div>
          <div style={S.grid3}>
            {[
              { label:"Monthly Income",    val:totalIncome,     bg:"#e0f2f1", color:"#004d40" },
              { label:"Budgeted Expenses", val:totalBudget,     bg:"#fff3e0", color:"#e65100" },
              { label:"EMI / Loans",       val:totalEMI,        bg:"#fce4ec", color:"#880e4f" },
              { label:"Budget Savings",    val:netSavings,      bg:"#e8f5e9", color:"#1b5e20" },
              { label:"Annual Savings",    val:netSavings * 12, bg:"#f3e5f5", color:"#4a148c" },
              { label:"Rent Income",       val:totalRent,       bg:"#e0f7fa", color:"#006064" },
            ].map((s,i) => (
              <div key={i} style={S.statBox(s.bg)}>
                <div style={S.statNum(s.color)}>{fmt(s.val)}</div>
                <div style={S.statLbl}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Current Month Actual vs Budget — linked to Monthly tab ── */}
          <div style={{ ...S.card("#00897b"), marginTop:8 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:8 }}>
              <div style={S.cardTitle("#00897b")}>📆 {MONTHS[CUR_MONTH]} {CUR_YEAR} — Actual vs Budget</div>
              <button
                onClick={() => setTab("monthly")}
                style={{ background:"#00897b", color:"#fff", border:"none", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontSize:"0.8rem", fontWeight:700 }}
              >+ Add Entry →</button>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10, marginBottom:16 }}>
              {[
                { label:"Budget",         val:totalBudget,             bg:"#fff3e0", color:"#e65100" },
                { label:"Actual Spent",   val:curMonthActual,          bg:"#e0f2f1", color:"#00695c" },
                { label: curMonthDiff >= 0 ? "Under Budget ✅" : "Over Budget ⚠️",
                  val:Math.abs(curMonthDiff),
                  bg: curMonthDiff >= 0 ? "#e8f5e9" : "#ffebee",
                  color: curMonthDiff >= 0 ? "#2e7d32" : "#c62828" },
                { label:"Entries Logged", val:curMonthLog.length + " items", bg:"#e8eaf6", color:"#1a237e", isText:true },
              ].map((s,i) => (
                <div key={i} style={{ ...S.statBox(s.bg), padding:12 }}>
                  <div style={{ ...S.statNum(s.color), fontSize:"1.2rem" }}>{s.isText ? s.val : fmt(s.val)}</div>
                  <div style={S.statLbl}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Category progress bars */}
            {EXP_CATS.map(cat => {
              const budgeted = data.expenses.filter(e => e.cat === cat).reduce((s, e) => s + Number(e.amount || 0), 0);
              const actual   = curMonthLog.filter(e => e.cat === cat).reduce((s, e) => s + Number(e.amount || 0), 0);
              if (budgeted === 0 && actual === 0) return null;
              const pct  = budgeted > 0 ? Math.min(100, Math.round(actual / budgeted * 100)) : 0;
              const over = actual > budgeted;
              return (
                <div key={cat} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                    <span style={{ fontSize:"0.82rem", fontWeight:700, color:CAT_COLORS[cat] }}>{cat}</span>
                    <span style={{ fontSize:"0.82rem", color:over?"#c62828":"#555" }}>
                      {fmt(actual)} / {fmt(budgeted)}
                      <span style={{ marginLeft:8, color:over?"#c62828":"#888" }}>({pct}%)</span>
                      {over && <span style={{ marginLeft:6 }}>⚠️</span>}
                    </span>
                  </div>
                  <div style={{ background:"#f0f0f0", borderRadius:6, height:8, overflow:"hidden" }}>
                    <div style={{ width:pct+"%", height:"100%", borderRadius:6, background:over?"#c62828":CAT_COLORS[cat], transition:"width 0.4s" }} />
                  </div>
                </div>
              );
            })}
            {curMonthLog.length === 0 && (
              <p style={{ color:"#bbb", fontSize:"0.84rem", textAlign:"center", margin:"10px 0 0" }}>
                No entries yet for {MONTHS[CUR_MONTH]}. Click "+ Add Entry" above to start logging.
              </p>
            )}
          </div>

          <div style={S.grid2}>
            <div style={S.card("#e65100")}>
              <div style={S.cardTitle("#e65100")}>📤 Budget by Category</div>
              {EXP_CATS.map(cat => {
                const total = data.expenses.filter(e => e.cat === cat).reduce((s,e) => s + Number(e.amount||0), 0);
                if (!total) return null;
                return <div key={cat} style={S.row}><span style={S.lbl}>{cat}</span><span style={S.val(CAT_COLORS[cat]||"#333")}>{fmt(total)}</span></div>;
              })}
              <div style={{ ...S.row, borderBottom:"none", marginTop:8 }}>
                <span style={{ ...S.lbl, color:"#e65100", fontSize:"0.95rem" }}>TOTAL BUDGET</span>
                <span style={{ ...S.val("#e65100"), fontSize:"1rem" }}>{fmt(totalBudget)}</span>
              </div>
            </div>
            <div style={S.card("#f57f17")}>
              <div style={S.cardTitle("#f57f17")}>📅 Upcoming Reminders</div>
              {data.reminders.slice(0,6).map(r => (
                <div key={r.id} style={S.row}>
                  <div><div style={S.lbl}>{r.label}</div><div style={{ fontSize:"0.75rem", color:"#999" }}>{r.dueDate}</div></div>
                  <span style={S.val("#f57f17")}>{r.amount > 0 ? fmt(r.amount) : "—"}</span>
                </div>
              ))}
            </div>
            <div style={S.card("#c62828")}>
              <div style={S.cardTitle("#c62828")}>🛡️ Insurance Premiums</div>
              {data.insurance.map(ins => (
                <div key={ins.id} style={S.row}>
                  <span style={S.lbl}>{ins.label}</span>
                  <span style={S.val("#c62828")}>{ins.premium > 0 ? fmt(ins.premium)+"/yr" : "Add"}</span>
                </div>
              ))}
              <div style={{ ...S.row, borderBottom:"none", marginTop:8 }}>
                <span style={{ ...S.lbl, color:"#c62828" }}>Total Annual</span>
                <span style={{ ...S.val("#c62828"), fontSize:"1rem" }}>{fmt(totalInsurance)}</span>
              </div>
            </div>
            <div style={S.card("#ad1457")}>
              <div style={S.cardTitle("#ad1457")}>🎉 Celebrations Budget</div>
              {data.celebrations.slice(0,6).map(c => (
                <div key={c.id} style={S.row}>
                  <div><div style={S.lbl}>{c.label}</div><div style={{ fontSize:"0.75rem", color:"#999" }}>{c.date}</div></div>
                  <span style={S.val("#ad1457")}>{fmt(c.budget)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ MONTHLY LOG ══ */}
      {tab === "monthly" && (
        <MonthlyTab
          data={data}
          monthlyLog={data.monthlyLog || []}
          onSave={(arr) => updateSection("monthlyLog", arr)}
        />
      )}

      {tab === "income" && (
        <SectionEditor title="💰 Income Sources" color="#2e7d32" items={data.income}
          fields={[
            { key:"label",  label:"Source Name",      type:"text" },
            { key:"amount", label:"Amount (₹/month)", type:"number" },
            { key:"status", label:"Status",           type:"select", options:["Active","Inactive"] },
          ]}
          onSave={(arr) => updateSection("income", arr)} />
      )}

      {tab === "expenses" && (
        <div>
          <div style={{ background:"#fff3e0", borderRadius:10, padding:"12px 16px", marginBottom:16, fontSize:"0.85rem", color:"#e65100", fontWeight:600 }}>
            These are your <strong>monthly budgeted</strong> amounts. To log actual spending, use the <strong>📆 Monthly Log</strong> tab.
          </div>
          {EXP_CATS.map(cat => (
            <div key={cat} style={S.card(CAT_COLORS[cat]||"#555")}>
              <div style={S.cardTitle(CAT_COLORS[cat])}>
                {cat} — {fmt(data.expenses.filter(e => e.cat===cat).reduce((s,e) => s+Number(e.amount||0),0))}/month
              </div>
              <SectionEditor title="" color={CAT_COLORS[cat]}
                items={data.expenses.filter(e => e.cat === cat)}
                fields={[
                  { key:"label",  label:"Item",       type:"text"   },
                  { key:"amount", label:"Amount (₹)", type:"number" },
                ]}
                defaultCat={cat}
                onSave={(arr) => {
                  const others = data.expenses.filter(e => e.cat !== cat);
                  updateSection("expenses", [...others, ...arr]);
                }}
                compact />
            </div>
          ))}
        </div>
      )}

      {tab === "emi" && (
        <SectionEditor title="🏦 EMI & Loans" color="#880e4f" items={data.emi}
          fields={[
            { key:"label",   label:"Loan Type",      type:"text"   },
            { key:"amount",  label:"EMI Amount (₹)", type:"number" },
            { key:"bank",    label:"Bank",           type:"text"   },
            { key:"dueDate", label:"Due Date",       type:"text"   },
            { key:"status",  label:"Status",         type:"select", options:["Active","Closed","None"] },
          ]}
          onSave={(arr) => updateSection("emi", arr)} />
      )}

      {tab === "rent" && (
        <SectionEditor title="🏠 Rent Income" color="#00838f" items={data.rent}
          fields={[
            { key:"label",   label:"Property",        type:"text"   },
            { key:"tenant",  label:"Tenant Name",     type:"text"   },
            { key:"amount",  label:"Rent Amount (₹)", type:"number" },
            { key:"dueDate", label:"Due Date",        type:"text"   },
            { key:"status",  label:"Status",          type:"select", options:["Active","Vacant"] },
          ]}
          onSave={(arr) => updateSection("rent", arr)} />
      )}

      {tab === "insurance" && (
        <SectionEditor title="🛡️ Insurance Policies" color="#c62828" items={data.insurance}
          fields={[
            { key:"label",    label:"Policy Name",        type:"text"   },
            { key:"company",  label:"Company",            type:"text"   },
            { key:"premium",  label:"Annual Premium (₹)", type:"number" },
            { key:"dueDate",  label:"Due Date",           type:"text"   },
            { key:"policyNo", label:"Policy Number",      type:"text"   },
          ]}
          onSave={(arr) => updateSection("insurance", arr)} />
      )}

      {tab === "reminders" && (
        <SectionEditor title="📅 Reminders & Due Dates" color="#f57f17" items={data.reminders}
          fields={[
            { key:"label",   label:"Reminder",   type:"text"   },
            { key:"dueDate", label:"Due Date",   type:"text"   },
            { key:"amount",  label:"Amount (₹)", type:"number" },
            { key:"cat",     label:"Category",   type:"text"   },
          ]}
          onSave={(arr) => updateSection("reminders", arr)} />
      )}

      {tab === "celebrations" && (
        <SectionEditor title="🎉 Celebrations & Festivals" color="#ad1457" items={data.celebrations}
          fields={[
            { key:"label",  label:"Event / Occasion", type:"text"   },
            { key:"date",   label:"Date / Month",     type:"text"   },
            { key:"budget", label:"Budget (₹)",       type:"number" },
            { key:"cat",    label:"Type",             type:"select", options:["Birthday","Anniversary","Festival","Leisure","Social","Function"] },
          ]}
          onSave={(arr) => updateSection("celebrations", arr)} />
      )}

      {tab === "travel" && (
        <SectionEditor title="✈️ Travel & Tours Log" color="#1a237e" items={data.travel}
          fields={[
            { key:"label",   label:"Destination", type:"text"   },
            { key:"date",    label:"Date",        type:"text"   },
            { key:"purpose", label:"Purpose",     type:"text"   },
            { key:"expense", label:"Expense (₹)", type:"number" },
          ]}
          onSave={(arr) => updateSection("travel", arr)} />
      )}

      {tab === "certificates" && (
        <SectionEditor title="📁 Certificates & Documents" color="#4a148c" items={data.certificates}
          fields={[
            { key:"label",    label:"Document Name",        type:"text" },
            { key:"person",   label:"Person",               type:"text" },
            { key:"issuer",   label:"Issuer / Institution", type:"text" },
            { key:"year",     label:"Year",                 type:"text" },
            { key:"location", label:"Storage Location",     type:"text" },
          ]}
          onSave={(arr) => updateSection("certificates", arr)} />
      )}

      {tab === "quick" && (
        <QuickButtonsTab buttons={data.customButtons} onSave={(arr) => updateSection("customButtons", arr)} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MONTHLY LOG TAB
══════════════════════════════════════════════════════ */
function MonthlyTab({ data, monthlyLog, onSave }) {
  const [selMonth, setSelMonth] = useState(CUR_MONTH);
  const [selYear,  setSelYear]  = useState(CUR_YEAR);
  const [adding,   setAdding]   = useState(false);
  const [viewYear, setViewYear] = useState(false);
  const [form, setForm] = useState({ label:"", cat:"Household", amount:"", date:"", note:"" });

  const yearOptions = [];
  for (let y = CUR_YEAR; y >= CUR_YEAR - 3; y--) yearOptions.push(y);

  const entries      = monthlyLog.filter(e => e.month === selMonth && e.year === selYear);
  const totalActual  = entries.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalBudget  = data.expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  const budgetByCat = {};
  const actualByCat = {};
  EXP_CATS.forEach(cat => {
    budgetByCat[cat] = data.expenses.filter(e => e.cat === cat).reduce((s,e) => s + Number(e.amount||0), 0);
    actualByCat[cat] = entries.filter(e => e.cat === cat).reduce((s,e) => s + Number(e.amount||0), 0);
  });

  // Year bar chart data
  const yearSummary = MONTHS.map((m, mi) => {
    const me = monthlyLog.filter(e => e.month === mi && e.year === selYear);
    return { month:m.slice(0,3), actual:me.reduce((s,e) => s+Number(e.amount||0),0), count:me.length };
  });
  const yearTotal   = yearSummary.reduce((s,m) => s+m.actual, 0);
  const maxMonthVal = Math.max(...yearSummary.map(m => m.actual), 1);

  const handleAdd = () => {
    if (!form.label.trim() || !form.amount) return alert("Fill in item and amount!");
    onSave([...monthlyLog, {
      id: Date.now(), month:selMonth, year:selYear,
      label:form.label.trim(), cat:form.cat,
      amount:Number(form.amount), date:form.date||"", note:form.note||"",
    }]);
    setForm({ label:"", cat:"Household", amount:"", date:"", note:"" });
    setAdding(false);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this entry?")) return;
    onSave(monthlyLog.filter(e => e.id !== id));
  };

  return (
    <div>
      {/* Controls */}
      <div style={{ display:"flex", gap:12, marginBottom:20, alignItems:"center", flexWrap:"wrap" }}>
        <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))}
          style={{ padding:"8px 14px", borderRadius:8, border:"1px solid #ddd", fontWeight:700, fontSize:"0.9rem", cursor:"pointer" }}>
          {MONTHS.map((m,i) => <option key={i} value={i}>{m}</option>)}
        </select>
        <select value={selYear} onChange={e => setSelYear(Number(e.target.value))}
          style={{ padding:"8px 14px", borderRadius:8, border:"1px solid #ddd", fontWeight:700, fontSize:"0.9rem", cursor:"pointer" }}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={() => setViewYear(!viewYear)}
          style={{ padding:"8px 16px", borderRadius:8, border:"none", background:viewYear?"#00897b":"#e0f2f1", color:viewYear?"#fff":"#00695c", fontWeight:700, cursor:"pointer", fontSize:"0.85rem" }}>
          {viewYear ? "▲ Hide Year View" : "📊 Year View"}
        </button>
      </div>

      {/* ── Year Bar Chart ── */}
      {viewYear && (
        <div style={S.card("#00897b")}>
          <div style={S.cardTitle("#00897b")}>📊 {selYear} — Month-wise Expenditure</div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:5, height:140, marginBottom:8 }}>
            {yearSummary.map((m,i) => {
              const h = m.actual > 0 ? Math.max(8, Math.round(m.actual / maxMonthVal * 120)) : 4;
              const isSel = i === selMonth;
              return (
                <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", cursor:"pointer" }}
                  onClick={() => { setSelMonth(i); setViewYear(false); }}>
                  <div style={{ fontSize:"0.6rem", color:"#666", marginBottom:2 }}>{m.actual > 0 ? "₹"+Math.round(m.actual/1000)+"k" : ""}</div>
                  <div style={{ width:"100%", height:h, background:isSel?"#00897b":m.actual>totalBudget?"#c62828":"#80cbc4",
                    borderRadius:"4px 4px 0 0", border:isSel?"2px solid #004d40":"none", transition:"height 0.3s" }} />
                  <div style={{ fontSize:"0.62rem", color:isSel?"#00695c":"#888", fontWeight:isSel?800:400, marginTop:3 }}>{m.month}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.82rem", color:"#666", paddingTop:8, borderTop:"1px solid #f0f0f0", flexWrap:"wrap", gap:8 }}>
            <span>Total {selYear} Actual: <strong style={{ color:"#00695c" }}>{fmt(yearTotal)}</strong></span>
            <span>Budget × 12: <strong style={{ color:"#e65100" }}>{fmt(totalBudget * 12)}</strong></span>
            <span>Diff: <strong style={{ color:yearTotal < totalBudget*12 ? "#2e7d32":"#c62828" }}>
              {yearTotal < totalBudget*12 ? "Saved " : "Over "}{fmt(Math.abs(totalBudget*12 - yearTotal))}
            </strong></span>
          </div>
        </div>
      )}

      {/* ── Month Detail Card ── */}
      <div style={S.card("#00897b")}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:8 }}>
          <div style={S.cardTitle("#00897b")}>{MONTHS[selMonth]} {selYear} — Actual vs Budget</div>
          <button onClick={() => setAdding(!adding)} style={S.addBtn("#00897b")}>
            {adding ? "✕ Cancel" : "+ Add Expenditure"}
          </button>
        </div>

        {/* Summary boxes */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10, marginBottom:16 }}>
          {[
            { label:"Budget",       val:totalBudget,   bg:"#fff3e0", color:"#e65100" },
            { label:"Actual Spent", val:totalActual,   bg:"#e0f2f1", color:"#00695c" },
            { label:totalActual<=totalBudget?"Under Budget ✅":"Over Budget ⚠️",
              val:Math.abs(totalBudget-totalActual),
              bg:totalActual<=totalBudget?"#e8f5e9":"#ffebee",
              color:totalActual<=totalBudget?"#2e7d32":"#c62828" },
          ].map((s,i) => (
            <div key={i} style={{ ...S.statBox(s.bg), padding:12 }}>
              <div style={{ ...S.statNum(s.color), fontSize:"1.2rem" }}>{fmt(s.val)}</div>
              <div style={S.statLbl}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Category progress bars */}
        <div style={{ marginBottom:16 }}>
          {EXP_CATS.map(cat => {
            const budgeted = budgetByCat[cat];
            const actual   = actualByCat[cat];
            if (!budgeted && !actual) return null;
            const pct  = budgeted > 0 ? Math.min(100, Math.round(actual/budgeted*100)) : 0;
            const over = actual > budgeted;
            return (
              <div key={cat} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                  <span style={{ fontSize:"0.82rem", fontWeight:700, color:CAT_COLORS[cat] }}>{cat}</span>
                  <span style={{ fontSize:"0.82rem", color:over?"#c62828":"#555" }}>
                    {fmt(actual)} / {fmt(budgeted)}
                    <span style={{ marginLeft:8, color:over?"#c62828":"#888" }}>({pct}%)</span>
                    {over && " ⚠️"}
                  </span>
                </div>
                <div style={{ background:"#f0f0f0", borderRadius:6, height:9, overflow:"hidden" }}>
                  <div style={{ width:pct+"%", height:"100%", borderRadius:6, background:over?"#c62828":CAT_COLORS[cat], transition:"width 0.4s" }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Entry Form */}
        {adding && (
          <div style={{ background:"#f0faf9", borderRadius:12, padding:16, marginBottom:16, border:"1px solid #b2dfdb" }}>
            <div style={{ fontWeight:700, color:"#00695c", marginBottom:12 }}>
              Add Expenditure — {MONTHS[selMonth]} {selYear}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div>
                <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Item / Description *</label>
                <input style={S.input} placeholder="e.g. Grocery shopping" value={form.label}
                  onChange={e => setForm({...form, label:e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Amount (₹) *</label>
                <input style={S.input} type="number" placeholder="0" value={form.amount}
                  onChange={e => setForm({...form, amount:e.target.value})} />
              </div>
              <div>
                <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Category</label>
                <select style={S.input} value={form.cat} onChange={e => setForm({...form, cat:e.target.value})}>
                  {EXP_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Date (optional)</label>
                <input style={S.input} type="date" value={form.date}
                  onChange={e => setForm({...form, date:e.target.value})} />
              </div>
              <div style={{ gridColumn:"span 2" }}>
                <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Note (optional)</label>
                <input style={S.input} placeholder="Any extra note..." value={form.note}
                  onChange={e => setForm({...form, note:e.target.value})} />
              </div>
            </div>
            <button style={S.addBtn("#00897b")} onClick={handleAdd}>✅ Add Entry</button>
          </div>
        )}

        {/* Entries list */}
        {entries.length === 0 ? (
          <p style={{ color:"#bbb", fontSize:"0.85rem", textAlign:"center", padding:"20px 0" }}>
            No entries for {MONTHS[selMonth]} {selYear}. Click "+ Add Expenditure" to start.
          </p>
        ) : (
          <div>
            <div style={{ fontWeight:700, color:"#555", fontSize:"0.85rem", marginBottom:8 }}>{entries.length} entries logged</div>
            {[...entries].sort((a,b) => (a.date||"").localeCompare(b.date||"")).map(entry => (
              <div key={entry.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 12px", background:"#f9fafb", borderRadius:8, marginBottom:7, border:"1px solid #e0e0e0", flexWrap:"wrap", gap:6 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:"#222", fontSize:"0.88rem" }}>{entry.label}</div>
                  <div style={{ fontSize:"0.75rem", color:"#888", marginTop:2 }}>
                    <span style={{ ...S.tag(CAT_COLORS[entry.cat]||"#777"), marginRight:8 }}>{entry.cat}</span>
                    {entry.date && <span style={{ marginRight:8 }}>📅 {entry.date}</span>}
                    {entry.note && <span>📝 {entry.note}</span>}
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontWeight:800, color:"#00695c", fontSize:"1rem" }}>{fmt(entry.amount)}</span>
                  <button style={S.deleteBtn} onClick={() => handleDelete(entry.id)}>🗑</button>
                </div>
              </div>
            ))}
            <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 12px 0", borderTop:"2px solid #e0e0e0", marginTop:6 }}>
              <span style={{ fontWeight:800, color:"#00695c" }}>Total Spent</span>
              <span style={{ fontWeight:900, color:"#00695c", fontSize:"1.1rem" }}>{fmt(totalActual)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── REUSABLE SECTION EDITOR ─────────────────────────────────────────────── */
function SectionEditor({ title, color, items, fields, onSave, compact=false, defaultCat }) {
  const [list,    setList]    = useState(items);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({});

  useEffect(() => { setList(items); }, [items]);

  const nextId       = () => Math.max(0, ...list.map(i => i.id||0)) + 1;
  const handleAdd    = () => {
    const ni = { id:nextId(), ...fields.reduce((acc,f) => ({ ...acc, [f.key]:f.type==="number"?0:"" }),{}) };
    if (defaultCat) ni.cat = defaultCat;
    const u = [...list, ni]; setList(u); onSave(u);
  };
  const handleEdit   = (item) => { setEditing(item.id); setForm({...item}); };
  const handleSave   = () => { const u=list.map(i=>i.id===editing?{...form,id:editing}:i); setList(u); onSave(u); setEditing(null); };
  const handleDelete = (id) => {
    if (!window.confirm("Delete?")) return;
    const u=list.filter(i=>i.id!==id); setList(u); onSave(u);
  };
  const iStyle = { ...S.input, marginBottom:6 };

  return (
    <div>
      {title && <div style={S.cardTitle(color)}>{title}</div>}
      {list.map(item => (
        <div key={item.id} style={{ background:"#f9fafb", borderRadius:10, padding:"12px 14px", marginBottom:10, border:"1px solid #e0e0e0" }}>
          {editing===item.id ? (
            <div>
              {fields.map(f => (
                <div key={f.key}>
                  <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>{f.label}</label>
                  {f.type==="select"
                    ? <select style={iStyle} value={form[f.key]||""} onChange={e => setForm({...form,[f.key]:e.target.value})}>
                        {f.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    : <input style={iStyle} type={f.type} value={form[f.key]||""}
                        onChange={e => setForm({...form,[f.key]:f.type==="number"?Number(e.target.value):e.target.value})} />
                  }
                </div>
              ))}
              <div style={{ display:"flex", gap:8, marginTop:6 }}>
                <button style={S.addBtn(color)} onClick={handleSave}>✅ Save</button>
                <button style={S.deleteBtn} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, color:"#1a237e", fontSize:"0.9rem" }}>{item.label||item.policy||"—"}</div>
                <div style={{ fontSize:"0.78rem", color:"#777", marginTop:2 }}>
                  {fields.filter(f=>f.key!=="label"&&item[f.key]).map(f => (
                    <span key={f.key} style={{ marginRight:12 }}>
                      <strong>{f.label}:</strong> {f.type==="number"&&item[f.key]>0?fmt(item[f.key]):item[f.key]||"—"}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button style={S.editBtn} onClick={() => handleEdit(item)}>✏️ Edit</button>
                <button style={S.deleteBtn} onClick={() => handleDelete(item.id)}>🗑</button>
              </div>
            </div>
          )}
        </div>
      ))}
      <button style={{ ...S.addBtn(color), marginTop:8 }} onClick={handleAdd}>+ Add New</button>
    </div>
  );
}

/* ── QUICK BUTTONS ───────────────────────────────────────────────────────── */
function QuickButtonsTab({ buttons, onSave }) {
  const [list,     setList]     = useState(buttons);
  const [newLabel, setNewLabel] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newType,  setNewType]  = useState("link");

  useEffect(() => { setList(buttons); }, [buttons]);

  const handleAdd    = () => {
    if (!newLabel.trim()||!newValue.trim()) return alert("Fill in label and value!");
    const u=[...list,{id:Math.max(0,...list.map(b=>b.id))+1,label:newLabel.trim(),value:newValue.trim(),type:newType}];
    setList(u); onSave(u); setNewLabel(""); setNewValue(""); setNewType("link");
  };
  const handleDelete = (id) => { const u=list.filter(b=>b.id!==id); setList(u); onSave(u); };

  return (
    <div>
      <div style={S.card("#37474f")}>
        <div style={S.cardTitle("#37474f")}>⚡ Quick Access Buttons</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12, marginBottom:24 }}>
          {list.map(btn => (
            <div key={btn.id} style={{ background:"#f0f4ff", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, border:"1px solid #c5cae9" }}>
              {btn.type==="link"  ? <a href={btn.value} target="_blank" rel="noreferrer" style={{ fontWeight:700, color:"#1565c0", fontSize:"0.9rem", textDecoration:"none" }}>{btn.label}</a>
              :btn.type==="phone" ? <a href={`tel:${btn.value}`} style={{ fontWeight:700, color:"#2e7d32", fontSize:"0.9rem", textDecoration:"none" }}>{btn.label} 📞</a>
              :<span style={{ fontWeight:700, color:"#37474f", fontSize:"0.9rem" }}>{btn.label}: {btn.value}</span>}
              <button style={S.deleteBtn} onClick={() => handleDelete(btn.id)}>🗑</button>
            </div>
          ))}
        </div>
        <div style={{ background:"#f5f7ff", borderRadius:12, padding:16 }}>
          <div style={{ fontWeight:700, color:"#37474f", marginBottom:12 }}>Add New Button</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 120px", gap:10 }}>
            <div>
              <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Label</label>
              <input style={S.input} placeholder="e.g. 🏦 SBI Net Banking" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Value / URL</label>
              <input style={S.input} placeholder="https://..." value={newValue} onChange={e => setNewValue(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize:"0.8rem", color:"#666", fontWeight:600 }}>Type</label>
              <select style={S.input} value={newType} onChange={e => setNewType(e.target.value)}>
                <option value="link">🔗 Link</option>
                <option value="phone">📞 Phone</option>
                <option value="note">📝 Note</option>
              </select>
            </div>
          </div>
          <button style={S.addBtn("#37474f")} onClick={handleAdd}>Add Button</button>
        </div>
      </div>
    </div>
  );
}
